import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'

import { computeDiffYAxis } from '~/pages/SupplyGap/models/diffChartAxis'
import { computeMainYAxis } from '~/pages/SupplyGap/models/mainChartAxis'
import {
  type GapSupplySeries,
  SUPPLY_TOTAL_DATA_KEY,
} from '~/pages/SupplyGap/models/supplyCategories'
import {
  CATEGORY_COLOR_MAP,
  type GapDiffChartPoint,
  type GapMainChartPoint,
  type GapTimePointRow,
  buildDiffChartPoints,
  buildMainChartPoints,
  collectMainAxisValues,
} from '~/pages/SupplyGap/models/types'

type Props = {
  points: GapTimePointRow[]
  /** aria-label 用のエリア名 (見出しテキストとしては表示しない。表示は呼び出し側の責務) */
  areaLabel: string
  /**
   * 供給 Bar の系列記述子 (AC-SDG-004b / AC-SDG-004c)。
   * 集約 = 供給力合計 1 本 / 内訳 = 表示中の全エリアから集めたカテゴリぶん。
   * 凡例 (呼び出し側) と Bar と tooltip が **同じ配列** を読むことで、
   * 順序・色・ラベルの 3 属性が構造的に一致する。
   */
  supplySeries: GapSupplySeries[]
}

const gridColor = LightModeSemanticColors.Border.lowEmphasis.value
const textColor = LightModeSemanticColors.Text.midEmphasis.value
const tooltipBg = LightModeSemanticColors.Surface.primary.value
const tooltipBorder = LightModeSemanticColors.Border.lowEmphasis.value
const axisColor = LightModeSemanticColors.Border.highEmphasis.value

// ツールチップ配色 (design `_sgGapTooltip` L626-629 準拠)
const tipText = LightModeSemanticColors.Text.highEmphasis.value
const tipMuted = LightModeSemanticColors.Text.midEmphasis.value
const gapLongColor = LightModeSemanticColors.Text.positiveHigh.value
const gapShortColor = LightModeSemanticColors.Text.negativeHigh.value
// design --shadow-lg (tokens.css L203)。ecloud に対応 token が無いため raw 値
const tipShadow =
  '0 10px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -2px rgba(0,0,0,0.04)'

const axisTick = { fontSize: 11, fill: textColor }
// Y 軸目盛は数値のみ (カンマ区切り)。単位「MW」は上段の Y 軸の上端 (一番上の目盛より上)
// に出す (8080 `supply/supply-gap.html` の YAxis `label: { position: 'top' }`、
// 2026-10-15)。 軸の内側 (insideTopLeft) に置くと最上部の目盛と重なるため、 上の余白
// (margin.top 22) を取って目盛の外に出す。
const yTickFormatter = (v: number): string => v.toLocaleString()

/** 上段の Y 軸の単位ラベル (8080 と同じ: 目盛と同じ 11px・mid-emphasis・軸の上端の外側)。 */
const mainYAxisUnitLabel = {
  value: 'MW',
  position: 'top' as const,
  offset: 8,
  fontSize: 11,
  fill: textColor,
}

const mwFmt = (v: number): string => `${Math.round(v).toLocaleString()} MW`

/**
 * time label が "HH:MM" (指定日 = 30 分コマ) のときは「コマ N HH:MM–HH:MM」に、
 * それ以外 (週間/月間の日付ラベル等) はそのまま返す。design `_sgGapTooltip` L631-632 準拠。
 */
const formatTooltipHeader = (timeLabel: string, t: TFunction): string => {
  const m = timeLabel.match(/^(\d{1,2}):(\d{2})$/)
  if (m === null) return timeLabel
  const hh = Number(m[1])
  const mn = Number(m[2])
  const koma = hh * 2 + (mn >= 30 ? 2 : 1)
  let endH = hh
  let endM = mn + 30
  if (endM >= 60) {
    endM -= 60
    endH += 1
  }
  const p2 = (n: number): string => String(n).padStart(2, '0')
  return `${t('page:supplyGap.tooltip.koma', { n: koma })}  ${timeLabel}–${p2(endH)}:${p2(endM)}`
}

/* eslint-disable ebalance/no-adhoc-typography -- chart tooltip の typography は
   design/supply/supply-gap.html の _sgGapTooltip (L620-660) が SoT で raw px 直書き
   体系 (13/16px の design スケール外値を含む)。ページ役割の textStyle 体系の対象外 */

type TipRow = { label: string; value: string }

/** 2 大計・調達源の 1 行 (label 左 muted / value 右)。 */
const SummaryRow = ({ label, value }: TipRow): React.ReactElement => (
  <Flex
    justifyContent="space-between"
    gap="16px"
    fontSize="sm"
    lineHeight="1.8"
  >
    <Text as="span" color={tipMuted}>
      {label}
    </Text>
    <Text
      as="span"
      color={tipText}
      css={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Text>
  </Flex>
)

/** 内訳行 (色スウォッチ + カテゴリ名 左 / MW 右)。design `_sgGapTooltip` の `comps`。 */
const BreakdownRow = ({
  label,
  value,
  color,
}: TipRow & { color: string }): React.ReactElement => (
  <Flex
    justifyContent="space-between"
    gap="16px"
    fontSize="sm"
    lineHeight="1.8"
  >
    <Flex as="span" alignItems="center" gap="6px" color={tipMuted}>
      <Box
        as="span"
        flexShrink={0}
        display="inline-block"
        width="10px"
        height="10px"
        background={color}
        borderRadius="sm"
      />
      {label}
    </Flex>
    <Text
      as="span"
      color={tipText}
      css={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Text>
  </Flex>
)

type MainTooltipProps = {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: GapMainChartPoint }>
  t: TFunction
  supplySeries: GapSupplySeries[]
}

/**
 * メインチャート専用ツールチップ (design `_sgGapTooltip` L620-660 相当)。
 * ギャップ(差分・方向)を見出し化し、供給力合計 / 需要+販売 の 2 大計を表示する。
 * いずれも backend 集計値 (supplyTotalMw / positionMw) をそのまま使い、
 * フロントで再計算しない。recharts が active / payload を注入する。
 *
 * 内訳表示では 2 大計の下にカテゴリ別 MW を色スウォッチ付きで足す
 * (design `_sgGapTooltip` の `if (!state.simplified)` 節 / mock-app の
 * `supplyMode === 'breakdown'` と同じ)。 積み上げ棒は面積でしか大小が読めないので、
 * これが無いと内訳に切り替えてもカテゴリ別の MW を数値で確認できない。
 * 並びは design 同様 **MW 降順** (どの調達源が大きいか一目)。値を持たないコマの
 * カテゴリは行ごと出さない (design の `filter(v != null)`)。
 */
const GapMainTooltip = ({
  active,
  payload,
  t,
  supplySeries,
}: MainTooltipProps): React.ReactElement | null => {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null
  }
  const point = payload[0]?.payload
  if (point === undefined) return null

  const breakdownRows = supplySeries
    .filter((s) => s.dataKey !== SUPPLY_TOTAL_DATA_KEY)
    .map((s) => ({ ...s, value: point[s.dataKey] }))
    .filter((s): s is GapSupplySeries & { value: number } => s.value != null)
    .sort((a, b) => b.value - a.value)

  const gap = point.position
  const isLong = gap >= 0
  const gapColor = isLong ? gapLongColor : gapShortColor
  const directionLabel = t(
    isLong ? 'page:supplyGap.tooltip.long' : 'page:supplyGap.tooltip.short',
  )
  // 値と方向ラベルの間は design 同様 全角スペース (U+3000)。リテラル直書きは
  // no-irregular-whitespace に触れるため escape で表現する
  const gapText = `${isLong ? '+' : '−'}${Math.abs(Math.round(gap)).toLocaleString()} MW\u3000${directionLabel}`

  return (
    <Box
      minWidth="200px"
      padding="8px 10px"
      background={tooltipBg}
      border={`1px solid ${tooltipBorder}`}
      borderRadius="md"
      boxShadow={tipShadow}
    >
      <Text
        marginBottom="4px"
        paddingBottom="3px"
        color={tipText}
        fontSize="sm"
        fontWeight="bold"
        borderBottom={`1px solid ${tooltipBorder}`}
      >
        {formatTooltipHeader(point.timeLabel, t)}
      </Text>
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        gap="16px"
        marginBottom="2px"
      >
        <Text as="span" color={tipMuted} fontSize="sm">
          {t('page:supplyGap.tooltip.gap')}
        </Text>
        <Text as="span" color={gapColor} fontSize="lg" fontWeight="bold">
          {gapText}
        </Text>
      </Flex>
      <SummaryRow
        label={t('page:supplyGap.tooltip.supplyTotal')}
        value={mwFmt(point.supplyTotal)}
      />
      <SummaryRow
        label={t('page:supplyGap.tooltip.demandPlusSales')}
        value={mwFmt(point.demandPlusSales)}
      />
      {breakdownRows.length > 0 && (
        <Box
          marginTop="4px"
          paddingTop="3px"
          borderTop={`1px solid ${tooltipBorder}`}
        >
          {breakdownRows.map((row) => (
            <BreakdownRow
              key={row.dataKey}
              color={row.color}
              label={row.label}
              value={mwFmt(row.value)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

type DiffTooltipProps = {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: GapDiffChartPoint }>
  t: TFunction
}

/**
 * 差分チャート専用ツールチップ (design `darkTooltipContent` charts.js L161 相当)。
 * ヘッダ (コマ + 時間レンジ) + ポジション値 1 行 (ロング/ショートの符号付き)。
 */
const GapDiffTooltip = ({
  active,
  payload,
  t,
}: DiffTooltipProps): React.ReactElement | null => {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null
  }
  const point = payload[0]?.payload
  if (point === undefined) return null
  const value = point.long !== 0 ? point.long : point.short
  const isLong = value >= 0
  const color = isLong ? CATEGORY_COLOR_MAP.LONG : CATEGORY_COLOR_MAP.SHORT
  const label = t(
    isLong ? 'page:supplyGap.tooltip.long' : 'page:supplyGap.tooltip.short',
  )

  return (
    <Box
      minWidth="140px"
      padding="8px 10px"
      background={tooltipBg}
      border={`1px solid ${tooltipBorder}`}
      borderRadius="md"
      boxShadow={tipShadow}
    >
      <Text
        marginBottom="4px"
        paddingBottom="3px"
        color={tipText}
        fontSize="sm"
        fontWeight="bold"
        borderBottom={`1px solid ${tooltipBorder}`}
      >
        {formatTooltipHeader(point.timeLabel, t)}
      </Text>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap="16px"
        fontSize="sm"
      >
        <Flex as="span" alignItems="center" gap="6px" color={tipMuted}>
          <Box
            as="span"
            flexShrink={0}
            display="inline-block"
            width="10px"
            height="10px"
            background={color}
            borderRadius="sm"
          />
          {label}
        </Flex>
        <Text
          as="span"
          color={color}
          fontWeight="bold"
          css={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {mwFmt(value)}
        </Text>
      </Flex>
    </Box>
  )
}

/* eslint-enable ebalance/no-adhoc-typography */

/**
 * design (`design/supply/supply-gap.html` `_renderMainInto` L662 / `_renderDiffInto` L693) 準拠で
 * チャートを上下 2 枚に分割する:
 * - 上段 main (flex 7): 供給 Bar (集約 = 供給力合計 1 本 / 内訳 = 受渡区分カテゴリの
 *   積上げ) + 需要 2 Line (需要想定+販売 / 需要想定)
 * - 下段 diff (flex 3): ポジション (ロング/ショート) Bar + y=0 ReferenceLine
 *
 * Y 軸は上下ともきりのいい刻み (1/2/5 × 10^n) で丸める (AC-SDG-006)。 上段 main は
 * 系列が非負なので片側レンジ (`computeMainYAxis`)、 下段 diff は 0 対称レンジ
 * (`computeDiffYAxis`、 AC-SDG-005)。 auto-scale のままだと全期間ショートの時に
 * 0 が上端へ潰れてロング側が描けない (issue #6314)。
 */
export const GapChart = ({
  points,
  areaLabel,
  supplySeries,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const mainData = buildMainChartPoints(points)
  const diffData = buildDiffChartPoints(points)
  // AC-SDG-006: メインチャートの Y 軸はきりのいい刻みの片側レンジ (系列は非負)。
  const mainYAxis = computeMainYAxis(collectMainAxisValues(points))
  // AC-SDG-005: 差分チャートの Y 軸は 0 を中心とした対称レンジ。
  const diffYAxis = computeDiffYAxis(points.map((pt) => pt.positionMw))

  // x 軸間引き (48 コマ等、点数が多い場合はラベルを間引く。design `_xAxisProps` 準拠)
  const tickInterval =
    points.length > 14 ? Math.ceil(points.length / 14) - 1 : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 上段 main: 供給積み上げ + 需要 2 線 (flex 7) */}
      <div
        aria-label={t('page:supplyGap.mainChartAriaLabel', { areaLabel })}
        role="group"
        style={{ flex: 7, minHeight: 0, width: '100%' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            // top 22: Y 軸の単位「MW」を一番上の目盛より上に出す分 (8080 と同じ)
            margin={{ top: 22, right: 16, left: 0, bottom: 4 }}
            data={mainData}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            {/* 上段は**時刻ラベルを出さない** (2026-09-03 デザイナー判断)。
                上下は同じ時間軸を共有しているので、 上段のラベルは情報を足さずに
                縦を約 30px 使うだけになる。 軸は下段の 1 本へ寄せる。
                design (`supply-gap.html` `_renderMainInto` / `_renderDiffInto`) は
                同一の `_xAxisProps` を上下で使い回して二重に出していたが、
                プロト側も本 PR に合わせて上段のラベルを外す。

                消し方は **`hide`** で、 `tick={false}` は使わない。 `CartesianGrid` の
                縦線は XAxis が算出した tick 位置から引かれるが、 `tick={false}` にすると
                その tick 配列が domain の両端 2 点まで縮み、 **縦線が 6 本 → 2 本に減る**
                (実測)。 `hide` は軸を描かないだけで tick の算出は残るため、 縦線は
                下段と同じ 6 本のまま揃う。 軸要素自体が描かれないので高さも自動で
                プロット領域へ返る (`height` の調整は不要)。 */}
            <XAxis
              dataKey="timeLabel"
              hide
              interval={tickInterval}
              tickLine={false}
            />
            {/* 下段と同じ `width={50}` 固定。 上段だけラベルを消してもプロットの
                左端が動かないのは、 この幅が両チャートで揃っているため。 */}
            {/* AC-SDG-006: きりのいい刻み (1/2/5 × 10^n)。 auto-scale だとエリア規模
                ごとに刻みがばらつく (沖縄 140MW で 0/35/70/105/140 = 実測)。 */}
            <YAxis
              width={50}
              domain={mainYAxis.domain}
              label={mainYAxisUnitLabel}
              tick={axisTick}
              tickFormatter={yTickFormatter}
              tickLine={false}
              ticks={mainYAxis.ticks}
            />
            <Tooltip
              // design 同様、tooltip が下段チャートに覆われないよう
              // y 方向は viewBox 外へ escape させ最前面に描画する
              allowEscapeViewBox={{ x: false, y: true }}
              content={<GapMainTooltip supplySeries={supplySeries} t={t} />}
              offset={16}
              wrapperStyle={{ zIndex: 60 }}
            />

            {/* 供給: 集約 = 供給力合計 1 本 (design 集約表示 = brand-cyan) /
                内訳 = 受渡区分カテゴリの積上げ (AC-SDG-004b)。 どちらも同じ
                系列記述子から描くので、 集約 / 内訳の分岐はここに無い
                (= 記述子が 1 本か複数かに退化している)。 `stackId` は 1 本でも
                害が無いので常に付ける (内訳が 1 カテゴリのエリアでも積上げは成立)。 */}
            {supplySeries.map((series) => (
              // 型引数は明示する: `dataKey` が union (`supplyTotal` |
              // `cat:<code>`) だと recharts が data 型を dataKey 側から推論して
              // 最初の member に潰すため。
              <Bar<GapMainChartPoint>
                key={series.dataKey}
                fill={series.color}
                dataKey={series.dataKey}
                isAnimationActive={false}
                name={series.label}
                stackId="supply"
              />
            ))}

            {/* 需要 2 線: design 同様 stepAfter (階段状)。 需要想定は破線で区別 */}
            <Line
              stroke={CATEGORY_COLOR_MAP.DEMAND_PLUS_SALES}
              dataKey="demandPlusSales"
              dot={false}
              isAnimationActive={false}
              name={t('page:supplyGap.legend.demandPlusSales')}
              strokeWidth={2}
              type="stepAfter"
            />
            <Line
              stroke={CATEGORY_COLOR_MAP.DEMAND_FORECAST}
              dataKey="demandForecast"
              dot={false}
              isAnimationActive={false}
              name={t('page:supplyGap.legend.demandForecast')}
              strokeDasharray="5 3"
              strokeWidth={2}
              type="stepAfter"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* 下段 diff: ポジション (ロング/ショート) + y=0 基準線 (flex 3、0 対称レンジ) */}
      <div
        aria-label={t('page:supplyGap.diffChartAriaLabel', { areaLabel })}
        role="group"
        style={{
          borderTop: `1px solid ${gridColor}`,
          flex: 3,
          minHeight: 0,
          width: '100%',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            data={diffData}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="timeLabel"
              interval={tickInterval}
              tick={axisTick}
              tickLine={false}
            />
            {/* AC-SDG-005 / AC-SDG-006: 0 対称・きりのいい刻み。 auto-scale だと
                全期間ショートの期間で 0 が上端に潰れ、 下の ReferenceLine が
                プロット領域の上辺と重なって切れる (issue #6314)。 */}
            <YAxis
              width={50}
              domain={diffYAxis.domain}
              tick={axisTick}
              tickFormatter={yTickFormatter}
              tickLine={false}
              ticks={diffYAxis.ticks}
            />
            <Tooltip content={<GapDiffTooltip t={t} />} offset={16} />
            <ReferenceLine stroke={axisColor} y={0} />
            {/* ロング / ショートは **同じ stack** に載せる (issue #7452)。

                stackId が無いと recharts は 1 コマの帯を系列数 (2) で割り、
                `offset = barCategoryGap + (size + barGap) * i` で各棒を左右へずらす。
                long は帯の左半分、 short は右半分に描かれるので

                  - 棒が帯の半分幅になり「細い」
                  - **符号によって棒の x 位置が半コマぶん動く**ため、 上段チャートの
                    同じコマと横位置が合わず、 コマが飛んでいるようにも見える

                という 2 つの誤読が同時に起きる。 値は排他 (`buildDiffChartPoints` が
                片方を必ず 0 にする) なので、 同じ stack に載せてもスタックは常に
                「0 → 非ゼロ」 の 1 本で、 符号混在の誤集計は起きない。 塗り分けと
                `radius` は系列ごとに残るので AC-SDG-005 の「視覚的に区別する」 も保つ。

                差分チャートは spec Discussion が「mock-app は SoT・React 実装は drift」
                と明記しており、 その mock-app (`SupplyDemandGap.razor.cs` の
                `GetDiffChartOptions`) も `Stacked = true` で 1 コマ 1 本にしている。 */}
            <Bar
              fill={CATEGORY_COLOR_MAP.LONG}
              dataKey="long"
              isAnimationActive={false}
              name={t('page:supplyGap.legend.long')}
              radius={[2, 2, 0, 0]}
              stackId="diff"
            />
            <Bar
              fill={CATEGORY_COLOR_MAP.SHORT}
              dataKey="short"
              isAnimationActive={false}
              name={t('page:supplyGap.legend.short')}
              radius={[0, 0, 2, 2]}
              stackId="diff"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
